/**
 * SelectField component - select input with options.
 * @module components/ui/Form/SelectField
 */

import { type ChangeEvent, type KeyboardEvent, type MouseEvent, useId } from "react";

import { Select } from "react-daisyui";

import { Button } from "../Button";
import { useEditLock, useLockedRefusal } from "../EditLock";
import type { FieldSize } from "./field-sizes";
import { ReadOnlyValue } from "./ReadOnlyValue";

export interface SelectOption {
  /** Option value */
  value: string;
  /** Option display label */
  label: string;
  /** Whether the option is disabled */
  disabled?: boolean;
}

function comparable(text: string): string {
  return text
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, " ")
    .trim();
}

/** The one enabled option a stored value resembles, by its first word; none when ambiguous. */
function resemblingOption(
  value: string,
  options: readonly SelectOption[],
): SelectOption | undefined {
  const stem = comparable(value).replace(/ .*$/, "").replace(/s$/, "");
  if (stem.length < 3) {
    return undefined;
  }
  const matches = options.filter(
    (option) =>
      !option.disabled &&
      comparable(option.label)
        .split(" ")
        .some((word) => word.startsWith(stem)),
  );
  return matches.length === 1 ? matches[0] : undefined;
}

export interface SelectFieldProps {
  /** Unique ID for the select */
  id: string;
  /** Current value */
  value: string;
  /** Change handler */
  onChange: (value: string) => void;
  /** Select options */
  options: SelectOption[];
  /** Placeholder option text */
  placeholder?: string;
  /**
   * Refuses a change but stays focusable, as `aria-disabled` with its picker
   * held shut: a natively disabled select leaves the tab order and its reason.
   */
  disabled?: boolean;
  /** Why it is disabled: a tooltip on hover and focus, and its description. */
  disabledReason?: string;
  /** Whether the field is required */
  required?: boolean;
  /** Size variant */
  size?: FieldSize;
  /** Additional CSS classes */
  className?: string;
  /** Test ID */
  "data-testid"?: string;
  /** Aria describedby ID */
  "aria-describedby"?: string;
  /** Accessible name, for a select with no visible `<label for>` of its own —
   *  a per-row control in a table, say, where the column header is the only
   *  visible clue and screen readers announce the control alone. */
  "aria-label"?: string;
  /** Set by `FormField` while it holds an error for this control. */
  "aria-invalid"?: boolean;
}

/**
 * SelectField component for dropdown selection.
 */
export function SelectField({
  id,
  value,
  onChange,
  options,
  placeholder = undefined,
  disabled: ownDisabled = false,
  required = false,
  size = undefined,
  className = "",
  "data-testid": testId,
  "aria-describedby": ariaDescribedby,
  "aria-label": ariaLabel,
  "aria-invalid": ariaInvalid = undefined,
  ...reasonProps
}: SelectFieldProps) {
  // Left in the rest so its presence is readable: a caller that can refuse
  // with a reason keeps one wrapper throughout, so the control is not
  // remounted, and does not lose focus, as the reason comes and goes.
  const { disabled, offersReason, disabledReason } = useLockedRefusal(ownDisabled, reasonProps);
  const reasonId = useId();
  const { locked } = useEditLock();
  const unlistedId = useId();
  const reason = disabled && disabledReason ? disabledReason : undefined;
  const unlisted = Boolean(value) && !options.some((option) => option.value === value);
  const describedBy =
    [ariaDescribedby, reason ? reasonId : undefined, unlisted ? unlistedId : undefined]
      .filter(Boolean)
      .join(" ") || undefined;
  if (locked) {
    // The option's label, not its value; an unlisted value reads as stored.
    const chosen = options.find((option) => option.value === value);
    return (
      <ReadOnlyValue
        id={id}
        value={value === "" ? "" : (chosen?.label ?? value)}
        data-testid={testId}
        aria-describedby={ariaDescribedby}
        aria-label={ariaLabel}
      />
    );
  }
  // daisyUI paints a refused select from `:disabled` only.
  const refused = disabled ? "cursor-not-allowed border-base-200 bg-base-200 text-muted" : "";

  const handleChange = (e: ChangeEvent<HTMLSelectElement>) => {
    if (disabled) {
      return;
    }
    onChange(e.target.value);
  };
  // A select has no `readOnly`, so the picker is held shut instead. An arrow
  // key can still move the value natively; `value` is controlled and reverts.
  const holdShut = (e: MouseEvent<HTMLSelectElement>) => {
    if (disabled) {
      e.preventDefault();
    }
  };
  const holdKeys = (e: KeyboardEvent<HTMLSelectElement>) => {
    if (disabled && e.key !== "Tab") {
      e.preventDefault();
    }
  };

  const select = (
    <Select
      id={id}
      value={value}
      onChange={handleChange}
      onMouseDown={holdShut}
      onKeyDown={holdKeys}
      aria-disabled={disabled || undefined}
      required={required}
      size={size}
      className={["w-full text-sm", refused, className].filter(Boolean).join(" ")}
      data-testid={testId}
      aria-describedby={describedBy}
      aria-label={ariaLabel}
      aria-invalid={ariaInvalid ?? (unlisted || undefined)}
      aria-required={required}
    >
      {[
        // With no placeholder an empty value names no option either, and the
        // field would read as the first one.
        ...(placeholder || (value === "" && !options.some((option) => option.value === ""))
          ? [
              <option key="__placeholder__" value="" disabled>
                {placeholder ?? "Not set"}
              </option>,
            ]
          : []),
        // React binds a value no option names to the first option, so the
        // field would state something the record does not hold.
        ...(unlisted
          ? [
              <option key="__unlisted__" value={value} disabled>
                {`Recorded as ‘${value}’`}
              </option>,
            ]
          : []),
        ...options.map((option) => (
          <option key={option.value} value={option.value} disabled={option.disabled}>
            {option.label}
          </option>
        )),
      ]}
    </Select>
  );

  const suggestion = unlisted && !disabled ? resemblingOption(value, options) : undefined;
  // Always a fragment, so the select keeps its node as the value leaves the list.
  const unlistedNote = unlisted ? (
    <span className="mt-1 flex flex-wrap items-center gap-2 text-xs">
      <span id={unlistedId} className="text-warning-strong">
        Recorded as &lsquo;{value}&rsquo;, which is not in the current list. Choose a replacement.
      </span>
      {suggestion ? (
        <Button size="xs" variant="outline" onClick={() => onChange(suggestion.value)}>
          Use {suggestion.label}
        </Button>
      ) : null}
    </span>
  ) : null;
  if (!offersReason) {
    return (
      <>
        {select}
        {unlistedNote}
      </>
    );
  }
  return (
    <div className={reason ? "tooltip block w-full" : "contents"} data-tip={reason}>
      {select}
      {unlistedNote}
      {reason ? (
        <span id={reasonId} className="sr-only">
          {reason}
        </span>
      ) : null}
    </div>
  );
}

export default SelectField;
